import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { searchIdpUsers } from "../api/rest";
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import css from "./IdpUserPicker.module.css";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Grant-by-email typeahead (ADR 176): type an email/name, pick a person, and the
 * committed value is their Keycloak sub. A raw UUID is still accepted (offered as
 * a direct pick), so power users and the no-lookup fallback keep working.
 */
export function IdpUserPicker({ value, onChange, id, placeholder, inputClassName }: {
  value: string;
  onChange: (sub: string) => void;
  id?: string;
  placeholder?: string;
  inputClassName?: string;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const debounced = useDebouncedValue(query, 250);

  const { data: results = [], isFetching } = useQuery({
    queryKey: ["idpUsers", debounced],
    queryFn: () => searchIdpUsers(debounced),
    enabled: debounced.trim().length >= 2,
  });

  const rawUuid = UUID_RE.test(query.trim()) ? query.trim() : null;
  const rawOption = rawUuid && !results.some((u) => u.sub === rawUuid) ? rawUuid : null;

  // Type the full email → commit its sub without a click (ADR 179): the deliberate
  // "type the email and it's granted" path. An effect, because it notifies the parent
  // once the async lookup resolves an exact-email match.
  const trimmedQuery = query.trim().toLowerCase();
  useEffect(() => {
    if (!trimmedQuery) return;
    const exact = results.find((u) => u.email.toLowerCase() === trimmedQuery);
    if (exact && value !== exact.sub) onChange(exact.sub);
  }, [results, trimmedQuery, value, onChange]);

  // Loud failure instead of a silent no-op: the lookup ran and matched nothing.
  const noMatch = query.trim().length >= 2 && !rawOption && !isFetching && results.length === 0;

  const pick = (sub: string, display: string) => {
    onChange(sub);
    setQuery(display);
    setOpen(false);
  };

  return (
    <div className={css.wrap}>
      <input
        id={id}
        className={inputClassName}
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        autoComplete="off"
        value={query}
        placeholder={placeholder}
        onChange={(e) => {
          const v = e.target.value;
          setQuery(v);
          setOpen(true);
          if (UUID_RE.test(v.trim())) onChange(v.trim());   // a pasted/typed sub commits directly
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {open && (results.length > 0 || rawOption) && (
        <ul className={css.list} role="listbox">
          {results.map((u) => (
            <li key={u.sub}>
              <button type="button" className={css.option} onClick={() => pick(u.sub, u.email || u.name)}>
                <span className={css.optName}>{u.name || u.email}</span>
                {u.email && <span className={css.optEmail}>{u.email}</span>}
              </button>
            </li>
          ))}
          {rawOption && (
            <li>
              <button type="button" className={css.option} onClick={() => pick(rawOption, rawOption)}>
                <span className={css.optName}>{t("idpPicker.useRawId")}</span>
                <span className={css.optEmail}>{rawOption}</span>
              </button>
            </li>
          )}
        </ul>
      )}
      {noMatch && <div className={css.noMatch}>{t("idpPicker.noMatch")}</div>}
      {value && !query && <div className={css.selected}>{t("idpPicker.selected", { sub: value })}</div>}
    </div>
  );
}
